/**
 * The review tracker: the totals and a three-segment bar in one line, and the counts the Review & submit step reuses.
 */
import type { ReviewTotals } from "../reviewState";
import { REVIEW_WORDS } from "../reviewWords";
import "./tracker.css";

/** "2 adjusted · 1 maintained · 4 to review": the sentence form of three counts (lower case inside a sentence). */
export const countsLine = (c: { readonly adjusted: number; readonly maintained: number; readonly toReview: number }): string =>
  `${c.adjusted} ${REVIEW_WORDS.adjusted.toLowerCase()} · ${c.maintained} ${REVIEW_WORDS.maintained.toLowerCase()} · ${c.toReview} ${REVIEW_WORDS.toReviewShort.toLowerCase()}`;

/** "All 5 open decisions handled: 1 adjusted, 4 maintained." for when nothing is left to review. */
export const handledLine = (c: { readonly adjusted: number; readonly maintained: number }): string => {
  const n = c.adjusted + c.maintained;
  return `${REVIEW_WORDS.all} ${n} ${REVIEW_WORDS.open} ${n === 1 ? REVIEW_WORDS.decision : REVIEW_WORDS.decisions} ${REVIEW_WORDS.handled}: ${c.adjusted} ${REVIEW_WORDS.adjusted.toLowerCase()}, ${c.maintained} ${REVIEW_WORDS.maintained.toLowerCase()}.`;
};

export function Bar({ totals }: { readonly totals: ReviewTotals }) {
  const share = (n: number): string => `${totals.total === 0 ? 0 : (n / totals.total) * 100}%`;
  return (
    <div className="gx-trk-bar" role="img" aria-label={countsLine(totals)} data-testid="tracker.bar">
      <span className="gx-trk-seg--adjusted" style={{ width: share(totals.adjusted) }} />
      <span className="gx-trk-seg--maintained" style={{ width: share(totals.maintained) }} />
    </div>
  );
}

/** `totals` given: only the states that occur are listed. */
export function Legend({ totals }: { readonly totals?: { readonly adjusted: number; readonly maintained: number; readonly toReview: number } }) {
  const has = (n: number): boolean => totals === undefined || n > 0;
  return (
    <ul className="gx-trk-legend" aria-hidden="true">
      {has(totals?.adjusted ?? 0) ? <li><span className="gx-trk-dot gx-trk-dot--adjusted" />{REVIEW_WORDS.adjusted}</li> : null}
      {has(totals?.maintained ?? 0) ? <li><span className="gx-trk-dot gx-trk-dot--maintained" />{REVIEW_WORDS.maintained}</li> : null}
      {has(totals?.toReview ?? 0) ? <li><span className="gx-trk-dot" />{REVIEW_WORDS.toReview}</li> : null}
    </ul>
  );
}

/** The review progress as one line (DESIGN §4.2): the counts, the bar and its legend, under the desk tabs on the Full sheet. Counts of decisions only, never a KPI value. */
export function Tracker({ totals }: { readonly totals: ReviewTotals }) {
  if (totals.total === 0) return null;
  return (
    <section className="gx-trk" aria-label="Decision review" data-testid="tracker">
      <p className="gx-trk-total" data-testid="tracker.total">{countsLine(totals)}</p>
      <Bar totals={totals} />
      <Legend />
    </section>
  );
}
